import { memo } from "react";
import { useI18n } from "@/i18n";
import { typography } from "@/design-system/tokens";

interface ConversionIndicatorProps {
  quantity: number;
  factor: number | null;
  exists: boolean | null;
  loading: boolean;
  fromUnitAbbr: string;
  toUnitAbbr: string;
}

/**
 * Shows conversion feedback below the quantity input:
 * - Loading: "Checking conversion..."
 * - Exists: "100 kg × 2.0 = 200.00 ud"
 * - Not exists: error message with link to settings
 */
export const ConversionIndicator = memo(function ConversionIndicator({
  quantity,
  factor,
  exists,
  loading,
  fromUnitAbbr,
  toUnitAbbr,
}: ConversionIndicatorProps) {
  const { t } = useI18n();

  // No conversion needed (same unit)
  if (exists === null) return null;

  if (loading) {
    return (
      <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#999", fontStyle: "italic" }}>
        {t.conversion.loading}
      </span>
    );
  }

  if (exists && factor !== null) {
    const result = (quantity * factor).toFixed(2);
    return (
      <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>
        → {quantity} {fromUnitAbbr} × {factor} = {result} {toUnitAbbr}
      </span>
    );
  }

  // No conversion available
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", borderRadius: "8px", background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#dc2626", fontWeight: typography.weight.semibold }}>
        ⚠️ {t.conversion.noConversion.replace("{fromUnit}", fromUnitAbbr).replace("{toUnit}", toUnitAbbr)}
      </span>
    </div>
  );
});
